import axios, { InternalAxiosRequestConfig, AxiosError } from 'axios';
import { emitApiError } from '@/utils/apiErrorEmitter';
import { invalidateWebsiteSession } from '@/utils/clientSessionState';

axios.interceptors.response.use(
    response => response,
    async (rawError: unknown) => {
        // Skip all global handling for cancelled requests — they are not errors.
        if (axios.isCancel(rawError)) {
            return Promise.reject(rawError);
        }

        const error = rawError as AxiosError & { config?: InternalAxiosRequestConfig };
        const originalConfig = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        const isProtectedRequest = originalConfig?.skipAuth !== true;
        if (error.response?.status === 401 && isProtectedRequest) {
            void invalidateWebsiteSession();
        }

        // --- Global error toast emission ---
        // No response at all — network failure, server unreachable, CORS, timeout, etc.
        // Honors disableToast === true so silent background requests stay silent;
        // the string[] form only suppresses server error codes, which never applies here.
        if (!error.response && originalConfig?.disableToast !== true) {
            emitApiError({ code: 'NetworkError' });
        }
        // Skip all toasts if disableToast === true
        if (originalConfig?.disableToast !== true) {
            const responseData = error.response?.data as { code?: string } | undefined;
            const errorCode = responseData?.code;
            const suppressedCodes = Array.isArray(originalConfig?.disableToast) ? originalConfig.disableToast : [];
            const isSuppressed = errorCode ? suppressedCodes.includes(errorCode) : false;

            if (!isSuppressed) {
                if (errorCode) {
                    emitApiError({ code: errorCode });
                } else if (error.response && error.response.status >= 400) {
                    emitApiError({ code: 'UnexpectedException' });
                }
            }
        }

        return Promise.reject(error);
    }
);

export default axios;
